import { useMemo } from 'react';

import { FILTER_TYPES, ROUTE_RESERVED_KEYWORDS } from 'operabase-router/constants';

import useAppContext from './useAppContext';

const useGetFilterTypes = clearExcludedTypes => {
  const { obRouteContext } = useAppContext();
  const { basePath, page } = obRouteContext;

  return useMemo(() => {
    const DEFAULT_FILTER_TYPES = [
      FILTER_TYPES.WORK_TYPE,
      FILTER_TYPES.LANGUAGE,
      FILTER_TYPES.COUNTRY,
      FILTER_TYPES.PERFORMANCE_HIGHLIGHT,
      FILTER_TYPES.PROFESSION,
      FILTER_TYPES.WORK,
      FILTER_TYPES.TICKET,
      FILTER_TYPES.WATCH_ONLINE,
      FILTER_TYPES.SURTITLE,
      FILTER_TYPES.ROLE_PROFESSION,
      FILTER_TYPES.COVER_ROLE,
      FILTER_TYPES.ROLE_DEBUT,
      FILTER_TYPES.PRODUCER_DEBUT,
      FILTER_TYPES.COMPOSER,
    ];

    const CLEAR_EXCLUDED_FILTERS = [
      FILTER_TYPES.WHO,
      FILTER_TYPES.WHERE,
      FILTER_TYPES.DATE,
      FILTER_TYPES.WHAT,
      FILTER_TYPES.GENRE,
      FILTER_TYPES.LETTER,
      FILTER_TYPES.SCORING_PARAMS,
      FILTER_TYPES.JUMP_IN_EXPRESSION,
      FILTER_TYPES.PRODUCTION_ID,
      FILTER_TYPES.SORT_CT,
      FILTER_TYPES.RENTALS,
      ...(clearExcludedTypes || []),
    ];

    if (basePath === ROUTE_RESERVED_KEYWORDS.artists) {
      CLEAR_EXCLUDED_FILTERS.push(FILTER_TYPES.PROFESSION);
    }

    if (basePath === ROUTE_RESERVED_KEYWORDS.organisations) {
      CLEAR_EXCLUDED_FILTERS.push(FILTER_TYPES.ORGANIZATION_TYPE);
    }

    // NOTE: staticFilterTypes is used to get filters stats from facet query
    // NOTE: excludeFilterTypes is used to exclude filters from the filter panel and applied filter chips
    if (basePath === ROUTE_RESERVED_KEYWORDS.casting) {
      if (page?.entity?.id) {
        return {
          staticFilterTypes: [
            ...DEFAULT_FILTER_TYPES.filter(
              type =>
                ![
                  FILTER_TYPES.PROFESSION,
                  FILTER_TYPES.SURTITLE,
                  FILTER_TYPES.TICKET,
                  FILTER_TYPES.WATCH_ONLINE,
                ].includes(type),
            ),
            FILTER_TYPES.COMPOSER,
          ],
          excludeFilterTypes: CLEAR_EXCLUDED_FILTERS,
        };
      }

      if (!page?.entity) {
        return {
          staticFilterTypes: [
            FILTER_TYPES.WORK_TYPE,
            FILTER_TYPES.LANGUAGE,
            FILTER_TYPES.COUNTRY,
            FILTER_TYPES.PERFORMANCE_HIGHLIGHT,
          ],
          excludeFilterTypes: [...CLEAR_EXCLUDED_FILTERS, FILTER_TYPES.BOOLEAN_SEARCH, FILTER_TYPES.SINCE_YEAR],
        };
      }
    }

    return {
      staticFilterTypes: DEFAULT_FILTER_TYPES,
      excludeFilterTypes: CLEAR_EXCLUDED_FILTERS,
    };
  }, [basePath, page?.entity, clearExcludedTypes]);
};

export const useHasFiltersApplied = () => {
  const { obRouteContext } = useAppContext();
  const { linkProps } = obRouteContext || {};
  const { filters } = linkProps || {};

  return useMemo(() => {
    const filtersApplied = Object.keys(filters || {});
    const FILTERS_TO_EXCLUDE = [
      FILTER_TYPES.WHO,
      FILTER_TYPES.WHERE,
      FILTER_TYPES.DATE,
      FILTER_TYPES.WHAT,
      FILTER_TYPES.GENRE,
      FILTER_TYPES.BOOLEAN_SEARCH,
      FILTER_TYPES.SINCE_YEAR,
      FILTER_TYPES.TICKET,
      FILTER_TYPES.WATCH_ONLINE,
      FILTER_TYPES.CTX_ENTITY,
      FILTER_TYPES.DATE_FROM,
      FILTER_TYPES.DATE_TO,
      FILTER_TYPES.PAGE,
      FILTER_TYPES.LETTER,
      FILTER_TYPES.SCORING_PARAMS,
      FILTER_TYPES.JUMP_IN_EXPRESSION,
      FILTER_TYPES.PRODUCTION_ID,
      FILTER_TYPES.SORT_CT,
    ];
    const hasFiltersApplied =
      filtersApplied?.length > 0 && filtersApplied.filter(filter => !FILTERS_TO_EXCLUDE.includes(filter)).length > 0;

    return hasFiltersApplied;
  }, [filters]);
};

export default useGetFilterTypes;
